import { Log, LogParserMetadata } from '@/parsing/Log';
import { LogEntry, LogEntryType } from '@/parsing/LogEntry';
import { LogLine } from '@/parsing/LogLine';
import { LogParser } from '@/parsing/LogParser';
import { SourceLog, SourceLogsBundle } from '@/parsing/SourceLogsBundle';

import { NetworkRequestOutcome } from '../common/NetworkRequestOutcome';
import { CommonParsingLogic } from '../CommonParsingLogic';
import type { ReactNativeLogEntry, ReactNativeTagType } from './types';

export const tagLabel: Record<ReactNativeTagType, string> = {
  navigation: 'Navigation',
  xhrReq: 'API Request',
  xhrRes: 'API Response',
  reduxAction: 'Action',
  appEvent: 'App Event',
  unhandled: 'Unhandled',
};

export const TRUNCATED_MARKER = '<truncated>';

interface ParsingContext {
  logEntries: LogEntry[];
  seenNetworkRequests: Map<string, { uniqueRequestId: number; logEntryIndex: number }>;
  currentUniqueRequestId: number;
}

export class ReactNativeLogParser implements LogParser {
  private onProgress?: (progress: number) => void;

  private static generateBlankParsingContext(): ParsingContext {
    return {
      logEntries: [],
      seenNetworkRequests: new Map(),
      currentUniqueRequestId: 1,
    };
  }

  async parse(bundle: SourceLogsBundle, onProgress: (progress: number) => void): Promise<Log> {
    this.onProgress = onProgress;

    const logEntriesPerFile: LogEntry[][] = await Promise.all(
      bundle.sourceLogs.map((f) => this.parseFile(f)),
    );

    const meta: LogParserMetadata = {
      originalFileNames: bundle.sourceLogs.map((f) => f.filePath),
      parser: 'react-native',
      app: 'eater-iOS/Android',
    };

    return await CommonParsingLogic.buildLogFromEntriesPerFile({
      logEntriesPerFile,
      parserMetadata: meta,
    });
  }

  private async parseFile(sourceLog: SourceLog): Promise<LogEntry[]> {
    // Each file gets its own local parsing context. This MUST be local (not
    // instance state) because parseFile calls run concurrently via Promise.all
    // and `await blob.text()` yields the event loop — a shared this.context
    // would be clobbered and reused across files, producing duplicate entries.
    const context = ReactNativeLogParser.generateBlankParsingContext();

    const blob = new Blob([sourceLog.contents], {
      type: 'text/plain; charset=utf-8',
    });
    const logText = await blob.text();

    const plainLines = logText.split('\n');

    const logLines: LogLine[] = [];
    plainLines.forEach((line, index) => {
      const trimmed = line.trim();
      if (trimmed.length > 0) {
        logLines.push({ lineIndex: index, content: trimmed });
      }
    });

    logLines.forEach((line, index) => {
      this.parseLine(line, context);

      if (this.onProgress) {
        const progress = index / logLines.length;
        this.onProgress(progress);
      }
    });

    return context.logEntries;
  }

  private parseLine(line: LogLine, context: ParsingContext) {
    if (context.logEntries.length === 0) {
      const entry = this.parseNewEntry(line, context);
      context.logEntries.push(entry);
      return;
    }

    const lastEntry = context.logEntries[context.logEntries.length - 1];

    try {
      const entry = this.parseNewEntry(line, context);
      context.logEntries.push(entry);
    } catch {
      this.appendLineToEntry(line, lastEntry);
    }
  }

  private appendLineToEntry(line: LogLine, entry: LogEntry) {
    entry.originalLines.push(line);
    if (entry.content) {
      entry.content = `${entry.content}\n${line.content}`;
    } else {
      entry.content = line.content;
    }
  }

  private parseNewEntry(line: LogLine, context: ParsingContext): LogEntry {
    const entry = this.parseLineToEntry(line, context);
    if (!entry) {
      throw `Cannot parse log entry from '${line.content}'`;
    }

    return entry;
  }

  private getSeverityLabel(levelStr: string): string {
    if (levelStr.toLowerCase().includes('w')) {
      return '⚠️';
    }
    if (levelStr.toLowerCase().includes('e')) {
      return '🚨';
    }
    return '';
  }

  private parseLineToEntry(line: LogLine, context: ParsingContext): LogEntry | null {
    const elements = line.content.split(' ');
    const dateStr = elements[0];
    const levelStr = elements[1];
    let jsonStr = elements.slice(2).join(' ');

    if (!dateStr || !levelStr || !jsonStr) {
      return null;
    }

    const date = this.parseDate(dateStr);

    if (jsonStr.endsWith(TRUNCATED_MARKER)) {
      jsonStr = jsonStr.slice(0, -TRUNCATED_MARKER.length);
    }

    const entry: LogEntry = {
      index: context.logEntries.length,
      entryType: LogEntryType.Generic,
      originalLines: [line],
      date,
      secondsSinceFirstLogEntry: 0,
      content: jsonStr,
      extra: {
        generic: {
          severity: this.getSeverityLabel(levelStr),
        },
      },
    };

    if (jsonStr.endsWith('...')) {
      return entry;
    }

    if (jsonStr) {
      try {
        const rnLogEntry: ReactNativeLogEntry = JSON.parse(jsonStr);

        // Set the module
        const moduleName = tagLabel[rnLogEntry.tag] || rnLogEntry.tag;
        entry.extra.generic!.module = moduleName;

        if (rnLogEntry.tag === 'reduxAction') {
          entry.entryType = LogEntryType.ReduxAction;
        } else if (rnLogEntry.tag === 'xhrReq') {
          entry.entryType = LogEntryType.NetworkRequest;

          entry.extra.networkRequest = {
            requestUniqueIndex: context.currentUniqueRequestId,
            url: new URL(rnLogEntry.data.url),
            outcome: NetworkRequestOutcome.Unknown,
            request: {
              logEntryIndex: entry.index,
              date,
              body: rnLogEntry.data.body,
              query: CommonParsingLogic.parseQueryFromUrl(rnLogEntry.data.url),
              method: rnLogEntry.data.method,
            },
          };

          // We want to save the request, so that retroactively we can link the request to the response
          context.seenNetworkRequests.set(rnLogEntry.data.requestId, {
            logEntryIndex: context.logEntries.length,
            uniqueRequestId: context.currentUniqueRequestId,
          });
          context.currentUniqueRequestId += 1;
        } else if (rnLogEntry.tag === 'xhrRes') {
          entry.entryType = LogEntryType.NetworkResponse;

          entry.extra.networkRequest = {
            requestUniqueIndex: context.currentUniqueRequestId,
            url: new URL(rnLogEntry.data.url),
            outcome:
              rnLogEntry.data.status >= 400
                ? NetworkRequestOutcome.Failed
                : NetworkRequestOutcome.Succeeded,
            response: {
              logEntryIndex: entry.index,
              date,
              httpCode: rnLogEntry.data.status,
              body: JSON.stringify(rnLogEntry.data.body),
              durationSec: rnLogEntry.data.timeInMs / 1000,
            },
          };

          // Let's try to link the response to a request
          const seenNetworkRequest = context.seenNetworkRequests.get(rnLogEntry.data.requestId);
          if (seenNetworkRequest) {
            entry.extra.networkRequest.requestUniqueIndex = seenNetworkRequest?.uniqueRequestId;
            const matchedRequestLogEntry = context.logEntries[seenNetworkRequest.logEntryIndex];

            // Set the request metadata to this response
            entry.extra.networkRequest.request =
              matchedRequestLogEntry.extra.networkRequest!.request;

            // Also set this response metadata to the request itself
            matchedRequestLogEntry.extra.networkRequest!.response =
              entry.extra.networkRequest.response;
            matchedRequestLogEntry.extra.networkRequest!.outcome =
              entry.extra.networkRequest.outcome;
          }
        }
      } catch (e) {
        console.error('FAILED TO PARSE LINE', e);
      }
    }

    return entry;
  }

  private parseDate(dateStr: string): Date {
    const resultDate = new Date(dateStr);

    if (!resultDate || isNaN(resultDate.getTime())) {
      throw `Cannot parse date from string "${dateStr}"`;
    }

    return resultDate;
  }
}
